import { useCallback, useEffect, useState } from 'react'
import { KeyRound } from 'lucide-react'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { EmptyRow, ErpTable, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { Pill } from '@/features/dossier/components/primitives'
import { listChannels, listProviders, saveChannel, updateChannel,
         type Channel, type Provider } from './api'
import { ChannelReady, Cost } from './components'

/**
 * Where messages go out from.
 *
 * ## No credential appears on this screen
 *
 * A channel is a sender id, a cap, a unit cost and a POINTER at an integration. The token lives
 * where every other credential in this system lives, behind the one HTTP door that rate-limits
 * and records — so a campaign's SMS spend shows up on the Integrations screen beside its model
 * spend, which is where an operator already looks.
 *
 * ## An unconfigured provider says so, here
 *
 * The alternative is a channel that looks ready, a campaign built on it, and the discovery at
 * 6am. Every driver this deployment has is listed, whether or not a channel uses it.
 *
 * ## The unit cost is per BILLABLE PART
 *
 * Not per message. One emoji in a template triples what every recipient costs, which is why the
 * part counter lives on the template screen and this number is per part.
 */
export function ChannelsPage() {
  const [rows, setRows] = useState<Channel[] | null>(null)
  const [providers, setProviders] = useState<Provider[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [adding, setAdding] = useState(false)

  const [providerCode, setProviderCode] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [senderId, setSenderId] = useState('')
  const [integrationId, setIntegrationId] = useState('')
  const [unitCost, setUnitCost] = useState('0.80')
  const [perMinuteCap, setPerMinuteCap] = useState(600)

  const load = useCallback(async () => {
    try {
      setError(null)
      setRows(await listChannels())
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => { void load() }, [load])
  useEffect(() => {
    void listProviders().then((p) => {
      setProviders(p)
      setProviderCode((c) => c || p[0]?.providerCode || '')
    }).catch(() => setProviders([]))
  }, [])

  if (error && !rows) return <ErrorStrip message={error} />
  if (!rows) return <LoadingBlock label="Loading channels" />

  const chosen = providers.find((p) => p.providerCode === providerCode)

  async function add(event: React.FormEvent) {
    event.preventDefault()
    if (!chosen) return
    setBusy(true); setError(null)
    try {
      await saveChannel({
        channelCode: chosen.channelCode,
        providerCode,
        displayName: displayName.trim(),
        integrationId: integrationId.trim() || null,
        senderId: senderId.trim() || null,
        replyTo: null,
        unitCostKes: unitCost ? Number(unitCost) : null,
        perMinuteCap,
        dailyCap: null,
        isDefault: false,
        active: true,
      })
      setAdding(false); setDisplayName(''); setSenderId(''); setIntegrationId('')
      await load()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Channels"
        description="The transports campaigns send on. Tokens are not stored here — a channel
                     points at an integration, and that is the only network door."
      />

      {error && <ErrorStrip message={error} />}

      <Panel>
        <div className="space-y-1.5 p-3">
          <p className="text-[11px] font-medium">Drivers on this deployment</p>
          <ul className="flex flex-wrap gap-2">
            {providers.map((p) => (
              <li key={p.providerCode}
                  className="rounded border border-border px-2 py-1">
                <span className="text-[11px]">{p.label}</span>
                <span className="ml-1.5">
                  <ChannelReady configured={p.configured} providerCode={p.providerCode} />
                </span>
                {!p.configured && p.requiredConfigKeys.length > 0 && (
                  <span className="block text-[10px] text-muted-foreground">
                    Its integration needs: {p.requiredConfigKeys.join(', ')}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      </Panel>

      <Panel>
        <div className="flex flex-wrap items-center justify-between gap-2 p-2">
          <p className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <KeyRound className="size-3.5" />
            Configure the provider credential under Integrations first, then point a channel at
            it here.
          </p>
          <button type="button" onClick={() => setAdding((v) => !v)}
                  className="rounded border border-border px-2 py-1 text-[11px]">
            {adding ? 'Close' : 'Add a channel'}
          </button>
        </div>

        {adding && (
          <form onSubmit={add} className="space-y-2 border-t border-border p-3">
            <div className="grid gap-2 sm:grid-cols-3">
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Provider
                </span>
                <select value={providerCode} onChange={(e) => setProviderCode(e.target.value)}
                        className="h-7 rounded border border-input bg-background px-2
                                   text-[12px]">
                  {providers.map((p) => (
                    <option key={p.providerCode} value={p.providerCode}>
                      {p.label}{p.configured ? '' : ' — not configured'}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Call it
                </span>
                <input value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                       required placeholder="Campaign SMS"
                       className="h-7 rounded border border-input bg-background px-2
                                  text-[12px]" />
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Sender id
                </span>
                <input value={senderId} onChange={(e) => setSenderId(e.target.value)}
                       placeholder="ZURIMATE"
                       className="h-7 rounded border border-input bg-background px-2
                                  text-[12px]" />
                <span className="text-[10px] text-muted-foreground">
                  What recipients see the message came from.
                </span>
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Cost per part (KES)
                </span>
                <input value={unitCost} onChange={(e) => setUnitCost(e.target.value)}
                       type="number" step="0.01" min="0"
                       className="h-7 rounded border border-input bg-background px-2
                                  text-[12px]" />
                <span className="text-[10px] text-muted-foreground">
                  Per BILLABLE PART. A long or emoji-bearing message costs several.
                </span>
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Messages a minute
                </span>
                <input type="number" min={1} value={perMinuteCap}
                       onChange={(e) => setPerMinuteCap(Number(e.target.value))}
                       className="h-7 rounded border border-input bg-background px-2
                                  text-[12px]" />
                <span className="text-[10px] text-muted-foreground">
                  Exceeding a shortcode's agreed rate is how a campaign loses the shortcode
                  mid-send.
                </span>
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Integration id
                </span>
                <input value={integrationId} onChange={(e) => setIntegrationId(e.target.value)}
                       className="h-7 rounded border border-input bg-background px-2
                                  text-[12px]" />
              </label>
            </div>
            <button type="submit" disabled={busy || !displayName.trim()}
                    className="rounded bg-primary px-3 py-1.5 text-[12px] font-medium
                               text-primary-foreground disabled:opacity-50">
              {busy ? 'Saving…' : 'Save the channel'}
            </button>
          </form>
        )}
      </Panel>

      <Panel>
        <ErpTable head={<>
          <Th>Channel</Th>
          <Th>Provider</Th>
          <Th className="axis-hide-sm">Sends as</Th>
          <Th className="axis-hide-sm">Cost</Th>
          <Th>State</Th>
        </>}>
          {rows.length === 0 && (
            <EmptyRow colSpan={5}
                      message="No channel configured. Campaigns can be drafted; they simply
                               have nowhere to go." />
          )}
          {rows.map((c) => (
            <tr key={c.id}>
              <Td>
                <span className="font-medium">{c.displayName}</span>
                {c.isDefault && (
                  <Pill className="ml-1 border-border">default</Pill>
                )}
              </Td>
              <Td>
                <span className="text-[11px]">{c.providerCode}</span>
                <span className="block text-[10px] text-muted-foreground">{c.channelCode}</span>
              </Td>
              <Td className="axis-hide-sm">
                <span className="text-[11px]">{c.senderId ?? '—'}</span>
              </Td>
              <Td className="axis-hide-sm">
                <Cost value={c.unitCostKes} />
                <span className="block text-[10px] text-muted-foreground">per part</span>
              </Td>
              <Td>
                <div className="flex flex-col gap-1">
                  <ChannelReady configured={c.configured} providerCode={c.providerCode} />
                  <button type="button" disabled={busy}
                          onClick={() => {
                            setBusy(true)
                            void updateChannel(c.id, {
                              channelCode: c.channelCode,
                              providerCode: c.providerCode,
                              displayName: c.displayName,
                              integrationId: c.integrationId,
                              senderId: c.senderId,
                              replyTo: c.replyTo,
                              unitCostKes: c.unitCostKes ? Number(c.unitCostKes) : null,
                              perMinuteCap: c.perMinuteCap,
                              dailyCap: c.dailyCap,
                              isDefault: c.isDefault,
                              active: !c.active,
                            }).then(load)
                              .catch((err) => setError(explain(err)))
                              .finally(() => setBusy(false))
                          }}
                          className="whitespace-nowrap rounded border border-border px-2
                                     py-0.5 text-[10px]">
                    {c.active ? 'Active — stand down' : 'Stood down — activate'}
                  </button>
                </div>
                {c.lastError && (
                  <span className="block text-[10px] text-rose-600">{c.lastError}</span>
                )}
              </Td>
            </tr>
          ))}
        </ErpTable>
      </Panel>
    </div>
  )
}
